<template>
  <div class="container-fluid" id="mapPage">
    <page-title
      :isDataLoading="isDataLoading"
      :title="this.$t('maps')"
      iconClasses="fas fa-map"
      :isTimelineDisplayed="true"
    />

    <form-controls
      v-if="controlsConfig.length > 0"
      :controls="controlsConfig"
      v-on:updateControl="handleFormControlChange"
      v-on:setDefaultValues="setFormControlsDefault"
      :displayConfig="{ toggleableGroups: ['filters'] }"
    />

    <div class="container-fluid mt-3 pt-2 mb-2 center-title">
      <h5 v-if="!downloaded" v-html="'&nbsp;'"></h5>
      <h5 v-if="!isSmallScreen && downloaded" v-html="indicatorLabel"></h5>
      <label v-if="isSmallScreen && downloaded" v-html="indicatorLabel"></label>
    </div>

    <!-- Adding SVG component -->
    <div
      :class="fullScreen && isSmartphoneDevice ? 'overlay' : 'container-fluid'"
      id="mapContainer"
    >
      <!-- SVG Map for common site -->
      <div
        :class="fullScreen && isSmartphoneDevice ? '' : 'row justify-content-md-center'"
        v-if="formControl.site !== 'france.paris.pcv'"
      >
        <svg-map
          :dataset="dataset"
          :routeForm="formControl"
          :startDate="start"
          :endDate="end"
        ></svg-map>
        <chart-config-menu
          :chart="dataset"
          :chartsData="chartsData"
          @showModal="() => {}"
        ></chart-config-menu>
      </div>

      <!-- Open street map for city tour -->
      <open-street-map
        :dataset="dataset"
        :routeForm="formControl"
        :startDate="start"
        :endDate="end"
        v-if="formControl.site === 'france.paris.pcv'"
      ></open-street-map>
      <chart-config-menu
        :chart="dataset"
        :chartsData="chartsData"
        @showModal="() => {}"
        v-if="formControl.site === 'france.paris.pcv'"
      ></chart-config-menu>
    </div>

    <!-- Bottom row, download button -->
    <div class="container-fluid mt-2" v-if="!isDataLoading">
      <div class="row">
        <button class="btn btn-chart mb-3 mx-auto" @click="downloadMapData">
          {{ $t('download.map_data') }}
          <i class="ml-2 fas fa-download"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import moment from 'moment'
import * as d3 from 'd3'
import sitesLevelsMap from '../assets/config/sites_levels_map.json'
import { utilsMixin, chartMixin } from '../utils/mixins'
import VueScrollTo from 'vue-scrollto'

//Scroll to
Vue.use(VueScrollTo, {
  container: 'body',
  duration: 500,
  easing: 'ease',
  offset: -100,
  force: true,
  cancelable: true,
  onStart: false,
  onDone: false,
  onCancel: false,
  x: false,
  y: true,
})

export default {
  name: 'Maps',
  dependencies: ['dataService', 'adminService'],
  mixins: [utilsMixin, chartMixin],
  props: ['period'],
  data() {
    return {
      downloaded: false,
      dataset: {},
      chartsData: {},
      timelineId: 'timeline',
      timeout: null,
      d3Map: null,
      canvasWidth: 1280,
      canvasHeight: 720,
      fullScreen: false,
      start: null,
      end: null,
      svg: null,
      formControl: {},
      controlsConfig: [],
    }
  },
  watch: {
    period: {
      handler() {
        this.start = moment(this.period[0], this.dateFormat).format('YYYY-MM-DD')
        this.end = moment(this.period[1], this.dateFormat).format('YYYY-MM-DD')
        this.fetchMapData()
      },
      immediate: true,
    },
  },
  mounted() {
    this.formControl['site'] = this.checkDefaultSite(this.$route.params.site)

    this.$emit('updateSite', [this.formControl.site])

    for (let key in this.configFilterHierarchy.filter_values) {
      if (!this.formControl[key] && key != 'site') {
        this.formControl[key] = 'all'
      }
    }

    this.adminService.getAllSitesWithMaps().then(data => {
      let resultSites = data.result
      if (this.getCurrentUser.userRole.name === 'partner') {
        resultSites = resultSites.filter(site => {
          const found = this.getCurrentUser.sites.find(userSite => {
            return site.site == userSite.site
          })
          return found ? true : false
        })
        if (!resultSites || resultSites.length == 0) {
          this.$router.replace('dashboard')
        }
      }

      this.controlsConfig = this.generateFormControlsConfig()
    })

    this.controlsConfig = this.generateFormControlsConfig()
  },
  beforeDestroy() {
    this.dataService.cancelDataRequest(this.currentRequestId)
  },
  computed: {
    mapSiteIDs() {
      return [this.formControl.site]
    },
    sites() {
      return sitesLevelsMap
    },
    formChildElementClass() {
      return {
        row: true,
        'justify-content-md-center': true,
        'col-auto': true,
      }
    },
    indicatorLabel() {
      let indicatorLabel = this.$t(`map_indicator.${this.dataset.dataType}`, [
        this.period[0],
        this.period[1],
      ])

      indicatorLabel += ` <strong>${
        this.dataset.indicator_unit == 'min'
          ? this.getVisitDurationLabel(
              this.dataset.indicator,
              'minutes',
              this.dataset.indicator > 60 ? 'HH:mm:ss' : 'mm:ss',
            )
          : this.dataset.indicator
      }</strong> `

      return indicatorLabel
    },
  },
  components: {
    'chart-config-menu': () => import('./utils/ChartConfigMenu'),
    'svg-map': () => import('./utils/SVGMap'),
    'open-street-map': () => import('./utils/OpenStreetMap'),
    'page-title': () => import('./utils/PageTitle'),
    'form-controls': () => import('./utils/FormControls'),
  },
  methods: {
    handleFormControlChange(change) {
      this.formControl[change.vModel] = change.value

      if (change.vModel == 'site') {
        this.controlsConfig = this.generateFormControlsConfig()
        this.$emit('updateSite', [this.formControl.site])
      }

      if (change.vModel === 'level') {
        this.chartsData.map['selected_level'] = this.formControl.level
      } else {
        this.fetchMapData()
      }
    },
    fetchMapData() {
      window.history.pushState(
        null,
        null,
        '/maps/site/' +
          (this.formControl.site || 'all') +
          '/level/' +
          (this.formControl.level || 'all') +
          '/language/' +
          (this.formControl.language || 'all') +
          '/histopad_rating/' +
          (this.formControl.histopad_rating || 'all') +
          '/visit_rating/' +
          (this.formControl.visit_rating || 'all') +
          '/collected_treasure_count/' +
          (this.formControl.collected_treasure_count || 'all') +
          '/start/' +
          this.start +
          '/end/' +
          this.end,
      )

      VueScrollTo.scrollTo('#mapPage')

      //Clearing routes when fetching new map data
      this.dataset.routes = []

      let found = this.sitesWithMap.find(swm => {
        return swm.site === this.formControl.site
      })

      if (found) {
        this.formControl['logVersionMajor'] =
          found.site === 'france.chambord.chateau' ||
          found.site === 'france.avignon.palais-des-papes'
            ? 4
            : found.log_version
      }

      //if (found.log_version == 4) {
      this.formControl['logVersionMinor'] = 2

      this.requestCount++

      let formControlsConfig = this.generateFormControlsConfig()

      let filters = formControlsConfig
        .filter(control => control.group === 'filters')
        .map(control => control.vModel)

      let filtersValues = {}
      filters.forEach(filter => (filtersValues[filter] = this.formControl[filter]))

      setTimeout(() => {
        this.dataService
          .getRoutes(this.formControl, filtersValues, this.start, this.end, this.currentRequestId)
          .then(data => {
            this.dataset = data.result
            this.dataset.type = 'map'
            this.chartsData['map'] = this.dataset
            this.chartsData.map.site_location = this.formControl.site
            this.chartsData.map['selected_level'] = this.formControl.level
            this.requestCount--
            this.downloaded = true
          })
          .catch(error => {
            if (error.message != 'cancel') {
              console.error('Error:', error)
              this.downloaded = false
            }
            this.requestCount--
          })

        this.currentRequestId = `route${JSON.stringify(this.formControl)}${this.start}${this.end}`
      }, 300)
    },
    toggleFullscreen() {
      this.fullScreen = !this.fullScreen

      let windowWidth = this.fullScreen ? window.innerWidth : 800,
        windowHeight = this.fullScreen ? window.innerHeight : 600

      if (this.fullScreen) {
        d3.select('#mapSVG .canvas').attr('viewBox', '0 0 ' + windowWidth + ' ' + windowHeight)
      } else {
        d3.select('#mapSVG .canvas').attr(
          'viewBox',
          '0 0 ' + this.canvasWidth + ' ' + this.canvasHeight,
        )
      }

      d3.selectAll('#mapSVG .background')
        .attr('height', windowHeight)
        .attr('width', windowWidth)
      d3.selectAll('#mapSVG .panCanvas')
        .attr('height', windowHeight)
        .attr('width', windowWidth)
    },
    getSiteDefaultLevel: function() {
      const siteFound = this.sitesWithMap.find(swm => {
        return swm.site == this.formControl.site
      })
      if (siteFound) {
        return siteFound.default_level
      } else {
        return '0'
      }
    },
    getSiteLevels: function() {
      const siteFound = this.sitesWithMap.find(swm => {
        return swm.site === this.formControl.site
      })
      if (siteFound) {
        if (!Array.isArray(siteFound.levels)) {
          siteFound.levels = siteFound.levels.split(',')
        }
        return siteFound.levels.map(level => ({
          value: String(level),
          label: String(level),
        }))
      } else {
        return []
      }
    },
    downloadMapData: function() {
      VueScrollTo.scrollTo('#mapPage')
      this.isLoading = true

      let formControlsConfig = this.generateFormControlsConfig()

      let filters = formControlsConfig
        .filter(control => control.group === 'filters')
        .map(control => control.vModel)
      let filtersValues = {}
      filters.forEach(filter => (filtersValues[filter] = this.formControl[filter]))

      this.dataService
        .downloadMapData(this.formControl, filtersValues, this.start, this.end, this.$locale)
        .then(data => {
          const url = window.URL.createObjectURL(new Blob([data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute(
            'download',
            `export_map_data_${moment().format('YYYY-MM-DD_HH:mm:ss')}.xls`,
          )
          document.body.appendChild(link)
          link.click()
          this.isLoading = false
        })
        .catch(e => {
          console.error('Error', e)
          this.isLoading = false
        })
    },
    generateFormControlsConfig() {
      // Set defaults for charts dependent upon other charts.
      if (this.formControl.level == null && this.formControl.site == null) {
        let defaultSite = this.sitesWithMap[0].site
        let defaultLevel = this.sitesWithMap.find(swm => {
          return swm.site == defaultSite
        }).default_level
        this.$set(this.formControl, 'site', defaultSite)
        this.$set(this.formControl, 'level', defaultLevel)
      } else if (this.formControl.level == null && this.formControl.site != null) {
        const siteFound = this.sitesWithMap.find(swm => {
          return swm.site == this.formControl.site
        })

        if (siteFound) {
          let defaultLevel = this.sitesWithMap.find(swm => {
            return swm.site == this.formControl.site
          }).default_level
          this.$set(this.formControl, 'level', defaultLevel)
        } else {
          this.$set(this.formControl, 'level', 0)
        }
      }

      let mapAvailableDatas = [
        { value: 'average_time', label: this.$t('chart.data_type.average_time') },
        { value: 'count', label: this.$t('chart.data_type.passages_count') },
        { value: 'percentage', label: this.$t('chart.data_type.percentage_passages_count') },
        {
          value: 'session_percentage',
          label: this.$t('chart.data_type.percentage_sessions_count'),
        },
        { value: 'session_count', label: this.$t('chart.data_type.session_count') },
      ]

      if (this.getCurrentUser.role !== 'partner') {
        mapAvailableDatas.push({ value: 'max_time', label: this.$t('chart.data_type.max_time') })
        mapAvailableDatas.push({ value: 'min_time', label: this.$t('chart.data_type.min_time') })
        mapAvailableDatas.push({ value: 'crash', label: this.$t('chart.data_type.is_crash') })
        mapAvailableDatas.push({
          value: 'warning_no_beacon_detected',
          label: this.$t('warning_type.no_beacon_detected'),
        })
        mapAvailableDatas.push({
          value: 'warning_device_overheating',
          label: this.$t('warning_type.device_overheating'),
        })
        mapAvailableDatas.push({
          value: 'warning_high_memory_usage',
          label: this.$t('warning_type.high_memory_usage'),
        })
      }

      // formControls config object
      let config = [
        {
          label: this.$t('maps_data'),
          type: 'select',
          vModel: 'dataType',
          default: 'average_time',
          options: mapAvailableDatas,
        },
        {
          label: this.$t('site.title'),
          type: 'select',
          vModel: 'site',
          default: this.checkDefaultSite(this.$route.params.site),
          options: this.sitesWithMap.map(site => ({
            value: site.site,
            label: site.name,
          })),
        },
        {
          label: this.$t('at'),
          type: 'select',
          vModel: 'level',
          default: this.getSiteDefaultLevel(),
          options: this.getSiteLevels(),
        },
        {
          label: this.$t('filter.by_language'),
          type: 'select',
          vModel: 'language',
          default: this.$route.params.language ? this.$route.params.language : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            ...this.getConfigFiltersFromSite(this.formControl.site).language.map(lang => ({
              value: lang,
              label: this.$t(`language.${lang}`),
            })),
          ],
        },
        {
          label: this.$t('filter.by_histopad_rating'),
          type: 'select',
          vModel: 'histopad_rating',
          default: this.$route.params.histopad_rating ? this.$route.params.histopad_rating : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            { value: '0', label: '0' },
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
            { value: '5', label: '5' },
          ],
        },
        {
          label: this.$t('filter.by_visit_rating'),
          type: 'select',
          vModel: 'visit_rating',
          default: this.$route.params.visit_rating ? this.$route.params.visit_rating : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            { value: '0', label: '0' },
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
            { value: '4', label: '4' },
            { value: '5', label: '5' },
          ],
        },
        {
          label: this.$t('filter.collected_treasure_count'),
          type: 'select',
          vModel: 'collected_treasure_count',
          default: this.$route.params.collected_treasure_count
            ? this.$route.params.collected_treasure_count
            : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            ...this.getConfigFiltersFromSite(this.formControl.site).collected_treasure_count.map(
              treasure => ({
                value: String(treasure),
                label: treasure,
              }),
            ),
          ],
        },
      ]

      if (this.getCurrentUser.role !== 'partner') {
        config.push({
          label: this.$t('chart.data_value.is_crash'),
          type: 'select',
          vModel: 'is_crash',
          default: this.$route.params.is_crash ? this.$route.params.is_crash : 'all',
          group: 'filters',
          options: [
            { value: 'all', label: this.$t('select_all') },
            { value: 1, label: this.$t('yes') },
            { value: 0, label: this.$t('no') },
          ],
        })
      }

      if (this.formControl.site === 'france.paris.pcv') {
        delete this.formControl.level
        config = config.filter(configItem => {
          return configItem.vModel !== 'level'
        })
      }

      return config
    },
    checkDefaultSite(site) {
      let defaultSite
      if (site) {
        let found = this.sitesWithMap.find(swm => {
          return swm.site == site
        })
        if (found) {
          defaultSite = site
        } else {
          defaultSite = this.sitesWithMap[0].site
        }
      } else {
        defaultSite = this.sitesWithMap[0].site
      }

      return defaultSite
    },
  },
}
</script>

<style scoped>
/* collapse */

.collapse-wrapper {
  width: 100%;
}
.collapse-wrapper .form-control,
button {
  background-color: #20343d !important;
}
.collapse-wrapper > div {
  padding: 20px;
  background-color: #2e4a57 !important;
}
</style>
